import { IonText } from '@ionic/react';
import { differenceInMinutes, isToday, isYesterday } from 'date-fns';
import { useMemo } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime, useIntl } from 'react-intl';

import styles from '@components/Activity.module.css';
import TertiaryButton from '@components/buttons/TertiaryButton';
import ClientAvatar from '@components/ClientAvatar';
import DateAndTimeStamp from '@components/dates/DateAndTimeStamp';
import ExpertAvatar from '@components/ExpertAvatar';
import SmallLabel from '@components/labels/SmallLabel';
import Nbsp from '@components/Nbsp';
import Hyperlink from '@components/typography/Hyperlink';
import useMyExpert from '@hooks/useMyExpert';
import useOpener from '@hooks/useOpener';
import useSanitizeHtml from '@hooks/useSanitizeHtml';
import { ActivityKindEnum, ActivityFragment as ActivityType } from '@typing/Generated';
import { journeyRoute } from '@utils/routeUtils';

type ActionItemType = Extract<ActivityType['resource'], { __typename: 'CarePlanActionItem' }>;
type ArticleType = Extract<ActivityType['resource'], { __typename: 'Article' }>;
type CareTeamInviteType = Extract<ActivityType['resource'], { __typename: 'CareTeamInvite' }>;
type WorksheetItemType = Extract<ActivityType['resource'], { __typename: 'WorksheetItem' }>;
type WorksheetType = Extract<ActivityType['resource'], { __typename: 'Worksheet' }>;
type ConsultType = Extract<ActivityType['resource'], { __typename: 'Consult' }>;
type DateTimeType = Extract<ActivityType['previousValue'], { __typename: 'DateTime' }>;
type EmployerBenefitType = Extract<ActivityType['resource'], { __typename: 'EmployerBenefit' }>;
type ExpertRoleType = Extract<ActivityType['resource'], { __typename: 'ExpertRole' }>;
type ExpertType = Extract<ActivityType['actor'], { __typename: 'Expert' }>;
type GoalType = Extract<ActivityType['resource'], { __typename: 'Goal' }>;
type ResourceType = Extract<ActivityType['resource'], { __typename: 'Resource' }>;
type SignedDocumentType = Extract<ActivityType['resource'], { __typename: 'SignedDocument' }>;
type StageType = Extract<ActivityType['previousValue'], { __typename: 'Stage' }>;
type TitleType = Extract<ActivityType['previousValue'], { __typename: 'Title' }>;

type Props = {
  activitiesForGroup: ActivityType[];
  activity: ActivityType;
};

enum ActorType {
  ADMIN = 'Admin',
  AUTOMATION = 'Automation',
  CLIENT = 'Client',
  EXPERT = 'Expert'
}

type ActorData = {
  firstName?: string | null;
  fullName?: string | null;
  lastName?: string | null;
  photoUrl?: string | null;
  type?: ActorType | null;
};

const Activity = ({ activitiesForGroup, activity }: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();

  const actorData = useMemo<ActorData>(() => {
    if (activity.actor?.__typename === 'Automation') {
      return {
        fullName: activity.actor.title,
        type: ActorType.AUTOMATION
      };
    }
    return {
      firstName: activity.actor?.firstName,
      fullName: activity.actor?.fullName,
      lastName: activity.actor?.lastName,
      photoUrl: activity.actor?.__typename === 'Expert' ? activity.actor?.photoUrl : undefined,
      type: activity.actor?.__typename as ActorType
    };
  }, [activity.actor]);

  const prevAndNewValuesForActivity = useMemo(() => {
    let previousValue = null;
    let newValue = null;

    switch (activity.kind) {
      case ActivityKindEnum.care_plan_action_item_changed:
        previousValue = activity.previousValue as TitleType | null;
        newValue = activity.newValue as TitleType | null;

        if (previousValue?.title && newValue?.title) {
          return {
            new: newValue?.title,
            prev: previousValue?.title
          };
        }
    }
  }, [activity.kind, activity.newValue, activity.previousValue]);
  // eslint-disable-next-line complexity
  const titleForActivity = useMemo(() => {
    let resource = null;
    let previousValue = null;
    let newValue = null;

    switch (activity.kind) {
      case ActivityKindEnum.consult_scheduled:
        resource = activity.resource as ConsultType;

        return intl.formatMessage(
          { id: 'models.activityFeed.consult_scheduled' },
          { fullName: actorData.fullName, startTime: <DateAndTimeStamp value={resource.startTime} /> }
        );
      case ActivityKindEnum.consult_updated:
        resource = activity.resource as ConsultType;

        return intl.formatMessage(
          { id: 'models.activityFeed.consult_updated' },
          { fullName: actorData.fullName, startTime: <DateAndTimeStamp value={resource.startTime} /> }
        );
      case ActivityKindEnum.consult_rescheduled:
        resource = activity.resource as ConsultType;
        previousValue = activity.previousValue as DateTimeType;
        newValue = activity.newValue as DateTimeType;

        return intl.formatMessage(
          { id: 'models.activityFeed.consult_rescheduled' },
          {
            fullName: actorData.fullName,
            previousStartTime: <DateAndTimeStamp value={previousValue.dateTime} />,
            startTime: <DateAndTimeStamp value={newValue.dateTime} />
          }
        );
      case ActivityKindEnum.consult_canceled:
        resource = activity.resource as ConsultType;

        return intl.formatMessage(
          { id: 'models.activityFeed.consult_canceled' },
          { fullName: actorData.fullName, startTime: <DateAndTimeStamp value={resource.startTime} /> }
        );
      case ActivityKindEnum.care_plan_action_item_added:
        resource = activity.resource as ActionItemType;

        return intl.formatMessage(
          { id: 'models.activityFeed.care_plan_action_item_added' },
          { fullName: actorData.fullName, title: resource.title.replace(/(<([^>]+)>)/gi, '') }
        );
      case ActivityKindEnum.care_plan_action_item_completed:
        resource = activity.resource as ActionItemType;

        return intl.formatMessage(
          { id: 'models.activityFeed.care_plan_action_item_completed' },
          { fullName: actorData.fullName, title: resource.title.replace(/(<([^>]+)>)/gi, '') }
        );
      case ActivityKindEnum.care_plan_action_item_incompleted:
        resource = activity.resource as ActionItemType;

        return intl.formatMessage(
          { id: 'models.activityFeed.care_plan_action_item_incompleted' },
          { fullName: actorData.fullName, title: resource.title.replace(/(<([^>]+)>)/gi, '') }
        );
      case ActivityKindEnum.employer_benefit_opened:
        resource = activity.resource as EmployerBenefitType;

        return intl.formatMessage(
          { id: 'models.activityFeed.employer_benefit_opened' },
          { fullName: actorData.fullName, title: resource.vendorName }
        );
      case ActivityKindEnum.goal_added:
        resource = activity.resource as GoalType;

        return intl.formatMessage(
          { id: 'models.activityFeed.goal_added' },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.goal_deleted:
        resource = activity.resource as GoalType;

        return intl.formatMessage(
          { id: 'models.activityFeed.goal_deleted' },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.goal_updated:
        resource = activity.resource as GoalType;

        return intl.formatMessage(
          { id: 'models.activityFeed.goal_updated' },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.resource_added:
        resource = activity.resource as ResourceType;

        return intl.formatMessage(
          { id: 'models.activityFeed.resource_added' },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.resource_changed:
        resource = activity.resource as ResourceType;

        return intl.formatMessage(
          { id: 'models.activityFeed.resource_changed' },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.resource_deleted:
        resource = activity.resource as ResourceType;

        return intl.formatMessage(
          { id: 'models.activityFeed.resource_deleted' },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.resource_marked_useful:
        resource = activity.resource as ResourceType;

        return intl.formatMessage(
          { id: 'models.activityFeed.resource_marked_useful' },
          {
            fullName: actorData.fullName,
            title: resource.title
          }
        );
      case ActivityKindEnum.resource_marked_not_useful:
        resource = activity.resource as ResourceType;

        return intl.formatMessage(
          { id: 'models.activityFeed.resource_marked_not_useful' },
          {
            fullName: actorData.fullName,
            title: resource.title
          }
        );
      case ActivityKindEnum.resource_notes_changed:
        resource = activity.resource as ResourceType;

        return intl.formatMessage(
          { id: 'models.activityFeed.resource_notes_changed' },
          {
            fullName: actorData.fullName,
            title: resource.title
          }
        );
      case ActivityKindEnum.journey_stage_changed:
        previousValue = activity.previousValue as StageType;
        newValue = activity.newValue as StageType;

        return intl.formatMessage(
          { id: 'models.activityFeed.care_plan_stage_changed' },
          {
            fullName: actorData.fullName,
            previousStage: intl.formatMessage({ id: `models.journey.stage.${previousValue.stage ?? 'none'}` }),
            stage: intl.formatMessage({ id: `models.journey.stage.${newValue.stage ?? 'none'}` })
          }
        );
      case ActivityKindEnum.care_plan_action_item_changed:
        resource = activity.resource as ActionItemType;
        previousValue = activity.previousValue as TitleType | null;
        newValue = activity.newValue as TitleType | null;

        return intl.formatMessage(
          { id: 'models.activityFeed.care_plan_action_item_changed' },
          { fullName: actorData.fullName, title: resource.title.replace(/(<([^>]+)>)/gi, '') }
        );
      case ActivityKindEnum.care_plan_action_item_deleted:
        resource = activity.resource as ActionItemType;

        return intl.formatMessage(
          { id: 'models.activityFeed.care_plan_action_item_deleted' },
          { fullName: actorData.fullName, title: resource.title.replace(/(<([^>]+)>)/gi, '') }
        );
      case ActivityKindEnum.care_plan_opened:
        return intl.formatMessage({ id: 'models.activityFeed.care_plan_opened' }, { fullName: actorData.fullName });
      case ActivityKindEnum.care_plan_lite_published:
        return intl.formatMessage(
          { id: 'models.activityFeed.care_plan_lite_published' },
          { fullName: actorData.fullName }
        );
      case ActivityKindEnum.worksheet_added:
        resource = activity.resource as WorksheetType;

        return intl.formatMessage(
          { id: 'models.activityFeed.worksheet_added' },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.worksheet_changed:
        resource = activity.resource as WorksheetType;

        return intl.formatMessage(
          { id: 'models.activityFeed.worksheet_changed' },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.worksheet_completed:
        resource = activity.resource as WorksheetType;

        return intl.formatMessage(
          { id: 'models.activityFeed.worksheet_completed' },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.worksheet_deleted:
        resource = activity.resource as WorksheetType;

        return intl.formatMessage(
          { id: 'models.activityFeed.worksheet_deleted' },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.worksheet_incompleted:
        resource = activity.resource as WorksheetType;

        return intl.formatMessage(
          { id: 'models.activityFeed.worksheet_incompleted' },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.worksheet_item_added:
        resource = activity.resource as WorksheetItemType;

        return intl.formatMessage(
          { id: 'models.activityFeed.worksheet_item_added' },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.worksheet_item_changed:
        resource = activity.resource as WorksheetItemType;

        return intl.formatMessage(
          { id: 'models.activityFeed.worksheet_item_changed' },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.worksheet_item_completed:
        resource = activity.resource as WorksheetItemType;

        return intl.formatMessage(
          { id: 'models.activityFeed.worksheet_item_completed' },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.worksheet_item_deleted:
        resource = activity.resource as WorksheetItemType;

        return intl.formatMessage(
          { id: 'models.activityFeed.worksheet_item_deleted' },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.worksheet_item_incompleted:
        resource = activity.resource as WorksheetItemType;

        return intl.formatMessage(
          { id: 'models.activityFeed.worksheet_item_incompleted' },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.care_team_invite_sent:
        resource = activity.resource as CareTeamInviteType;

        return intl.formatMessage(
          { id: 'models.activityFeed.care_team_invite_sent' },
          { email: resource.email, fullName: actorData.fullName }
        );
      case ActivityKindEnum.care_team_invite_canceled:
        resource = activity.resource as CareTeamInviteType;

        return intl.formatMessage(
          { id: 'models.activityFeed.care_team_invite_canceled' },
          { email: resource.email, fullName: actorData.fullName }
        );
      case ActivityKindEnum.expert_role_added:
        resource = activity.resource as ExpertRoleType;

        return intl.formatMessage(
          { id: 'models.activityFeed.expert_role_added' },
          { fullName: resource.expert.fullName }
        );
      case ActivityKindEnum.expert_role_deleted:
        resource = activity.resource as ExpertRoleType;

        return intl.formatMessage(
          { id: 'models.activityFeed.expert_role_deleted' },
          { fullName: resource.expert.fullName }
        );
      case ActivityKindEnum.safety_quiz_started:
      case ActivityKindEnum.pme_started:
      case ActivityKindEnum.legal_tracker_started:
      case ActivityKindEnum.provider_tracker_started:
      case ActivityKindEnum.medication_tracker_started:
        return intl.formatMessage(
          { id: `models.activityFeed.assessment_started.${activity.kind}` },
          { fullName: actorData.fullName }
        );
      case ActivityKindEnum.safety_quiz_completed:
      case ActivityKindEnum.pme_completed:
      case ActivityKindEnum.legal_tracker_completed:
      case ActivityKindEnum.provider_tracker_completed:
      case ActivityKindEnum.medication_tracker_completed:
        return intl.formatMessage(
          { id: `models.activityFeed.assessment_completed.${activity.kind}` },
          { fullName: actorData.fullName }
        );
      case ActivityKindEnum.community_opened:
        return intl.formatMessage({ id: 'models.activityFeed.community_opened' }, { fullName: actorData.fullName });
      case ActivityKindEnum.article_opened:
        resource = activity.resource as ArticleType;
        const articleActivities = activitiesForGroup.filter(
          activity => activity.kind === ActivityKindEnum.article_opened
        );

        return intl.formatMessage(
          { id: 'models.activityFeed.article_opened' },
          {
            fullName: actorData.fullName,
            title: articleActivities.map((activity, index) => {
              const articleResource = activity.resource as ArticleType;
              return (
                <IonText key={activity.id}>
                  {articleResource.title}
                  {index < articleActivities.length - 1 && (
                    <>
                      <FormattedMessage id="dictionary.punctuation.comma" />
                      <Nbsp />
                    </>
                  )}
                </IonText>
              );
            })
          }
        );
      case ActivityKindEnum.article_video_played:
        resource = activity.resource as ArticleType;
        return intl.formatMessage(
          { id: `models.activityFeed.article_video_played` },
          { fullName: actorData.fullName, title: resource.title }
        );
      case ActivityKindEnum.signed_document_completed:
        resource = activity.resource as SignedDocumentType;
        return intl.formatMessage(
          { id: `models.activityFeed.signed_document_completed` },
          { documentFor: resource.documentFor, fullName: actorData.fullName }
        );
      case ActivityKindEnum.signed_document_faxed:
        resource = activity.resource as SignedDocumentType;
        return intl.formatMessage(
          { id: `models.activityFeed.signed_document_faxed` },
          { documentFor: resource.documentFor, fullName: actorData.fullName }
        );
      case ActivityKindEnum.signed_document_revoked:
        resource = activity.resource as SignedDocumentType;
        return intl.formatMessage(
          { id: `models.activityFeed.signed_document_revoked` },
          { documentFor: resource.documentFor, fullName: actorData.fullName }
        );
      case ActivityKindEnum.signed_document_sent:
        resource = activity.resource as SignedDocumentType;
        return intl.formatMessage(
          { id: `models.activityFeed.signed_document_sent` },
          { documentFor: resource.documentFor, fullName: actorData.fullName }
        );
      default:
        return undefined;
    }
  }, [activity, activitiesForGroup, actorData.fullName, intl]);

  const changesOpener = useOpener();

  const displayTime = useMemo(() => {
    const defaultDate = new Date();
    const minutesDifference = differenceInMinutes(defaultDate, activity.activeAt);

    if (minutesDifference < 60) {
      return intl.formatMessage(
        { id: 'pages.expert.activity.daysAndTime.minutesAgo' },
        { minutes: minutesDifference, time: <FormattedTime value={activity.activeAt} /> }
      );
    }
    if (minutesDifference < 180) {
      return intl.formatMessage(
        { id: 'pages.expert.activity.daysAndTime.hoursAgo' },
        { hours: Math.floor(minutesDifference / 60), time: <FormattedTime value={activity.activeAt} /> }
      );
    }

    if (isToday(activity.activeAt)) {
      return intl.formatMessage(
        { id: 'pages.expert.activity.daysAndTime.today' },
        {
          date: <FormattedDate day="numeric" month="long" value={activity.activeAt} year="numeric" />,
          time: <FormattedTime value={activity.activeAt} />
        }
      );
    }

    if (isYesterday(activity.activeAt)) {
      return intl.formatMessage(
        { id: 'pages.expert.activity.daysAndTime.yesterday' },
        {
          date: <FormattedDate day="numeric" month="long" value={activity.activeAt} />,
          time: <FormattedTime value={activity.activeAt} />
        }
      );
    }
    return <DateAndTimeStamp value={activity.activeAt} />;
  }, [activity.activeAt, intl]);

  const sanitizedPrevValue = useSanitizeHtml(prevAndNewValuesForActivity?.prev, activity.journeyId, activity);

  const sanitizedNewValue = useSanitizeHtml(prevAndNewValuesForActivity?.new, activity.journeyId, activity);

  if (titleForActivity) {
    return (
      <div className={styles.container}>
        <div className={styles.avatar}>
          {activity.actor && (
            <>
              {actorData.type === ActorType.EXPERT && <ExpertAvatar expert={activity.actor as ExpertType} size={30} />}
              {actorData.type === ActorType.CLIENT && actorData.firstName && actorData.lastName && (
                <ClientAvatar
                  clientId={null}
                  color="primary500"
                  firstName={actorData.firstName}
                  journeyId={null}
                  lastName={actorData.lastName}
                  size={30}
                />
              )}
            </>
          )}
        </div>
        <div>
          <IonText className={styles.text}>
            {titleForActivity}
            {activity.journey && (
              <>
                <Nbsp />
                {intl.formatMessage(
                  { id: 'models.activityFeed.forJourney' },
                  {
                    journey: (
                      <Hyperlink
                        className={styles.bold}
                        routerLink={journeyRoute({ journeyId: activity.journey.id, linkAfterJourney: '', myExpert })}
                      >
                        {activity.journey.careProfile.name ?? activity.journey.owner.fullName}
                      </Hyperlink>
                    )
                  }
                )}
              </>
            )}
          </IonText>
          <div>
            <SmallLabel>{displayTime}</SmallLabel>
          </div>
          {prevAndNewValuesForActivity && (
            <>
              {changesOpener.isOpen && (
                <>
                  <div className="ion-margin-top">
                    <SmallLabel>
                      <FormattedMessage id="dictionary.previous" />
                    </SmallLabel>
                  </div>
                  <div className={styles.previous}>{sanitizedPrevValue}</div>
                  <div>
                    <SmallLabel>
                      <FormattedMessage id="dictionary.new" />
                    </SmallLabel>
                  </div>
                  <div className={styles.new}>{sanitizedNewValue}</div>
                </>
              )}
              <div>
                <TertiaryButton
                  icon={changesOpener.isOpen ? 'chevronUp' : 'chevronDown'}
                  onClick={changesOpener.toggle}
                  textKey={changesOpener.isOpen ? 'models.activity.closeDetails' : 'models.activity.openDetails'}
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
  return null;
};

export default Activity;
